/**
 * Custom Site Specific Javascript
 *
 * This file is to add custom features to the website without having to modify
 * core/common assets. Define a custom class, and build new or overwrite
 * existing methods to create custom functionality.
 */

import { toggleFieldError } from "commonAssets/lib/toggleFieldError";

const OOP_CLAIM_SELECTOR = 'input[name="documented_out_of_pocket_claim"]';
const PRO_RATA_CLAIM_SELECTOR = 'input[name="pro_rata_cash_payment"]';
const TRANSACTION_TEXT_MIN_LENGTH = 3;
const TRANSACTION_MIN_DATE = "2000-01-01";
const TOTAL_AMOUNT_SELECTOR = "#total_oop_amount";

const COST_TYPE_SELECTOR =
    'input[id^="documented_oop_cost_type"], input[name^="documented_oop_cost_type"]';
const DESCRIPTION_SELECTOR =
    'textarea[id^="documented_oop_description"], textarea[name^="documented_oop_description"]';
const AMOUNT_SELECTOR =
    'input[id^="documented_oop_amount"], input[name^="documented_oop_amount"]';
const DATE_SELECTOR =
    'input[id^="documented_oop_date"], input[name^="documented_oop_date"]';

function getValidationTokens(field) {
    const current = field.getAttribute("data-validate") || "";

    return new Set(
        current
            .split(" ")
            .map((token) => token.trim())
            .filter(Boolean)
    );
}

function ensureValidationToken(field, token) {
    const tokens = getValidationTokens(field);
    tokens.add(token);
    field.setAttribute("data-validate", Array.from(tokens).join(" "));
}

function getTomorrowIsoDate() {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);

    const year = tomorrow.getFullYear();
    const month = String(tomorrow.getMonth() + 1).padStart(2, "0");
    const day = String(tomorrow.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
}

function syncTotalOopAmount() {
    const totalField = document.querySelector(TOTAL_AMOUNT_SELECTOR);
    if (!totalField) {
        return;
    }

    const total = Array.from(document.querySelectorAll(AMOUNT_SELECTOR)).reduce(
        (sum, field) => {
            const val = parseFloat(field.value);
            return sum + (isNaN(val) ? 0 : val);
        },
        0
    );

    totalField.value = total > 0 ? total.toFixed(2) : "";
}

function applyTransactionFieldValidation() {
    const maxDate = getTomorrowIsoDate();

    document.querySelectorAll(COST_TYPE_SELECTOR).forEach((field) => {
        field.setAttribute("data-min-length", `${TRANSACTION_TEXT_MIN_LENGTH}`);
        ensureValidationToken(field, "min-length");
    });

    document.querySelectorAll(DESCRIPTION_SELECTOR).forEach((field) => {
        field.setAttribute("data-min-length", `${TRANSACTION_TEXT_MIN_LENGTH}`);
        ensureValidationToken(field, "min-length");
    });

    document.querySelectorAll(AMOUNT_SELECTOR).forEach((field) => {
        field.setAttribute("min", "0.01");
        field.setAttribute("step", "0.01");

        if (!field.dataset.totalBound) {
            field.dataset.totalBound = "true";
            field.addEventListener("input", syncTotalOopAmount);
        }
    });

    document.querySelectorAll(DATE_SELECTOR).forEach((field) => {
        field.setAttribute("min", TRANSACTION_MIN_DATE);
        field.setAttribute("max", maxDate);
        field.setAttribute("data-date-min", TRANSACTION_MIN_DATE);
        field.setAttribute("data-date-max", maxDate);
        ensureValidationToken(field, "date");
        ensureValidationToken(field, "date-min");
        ensureValidationToken(field, "date-max");
    });
}

function bindTransactionValidationForDynamicRows() {
    if (document.body.dataset.avisTransactionValidationBound === "true") {
        return;
    }

    document.body.dataset.avisTransactionValidationBound = "true";

    document.body.addEventListener("click", (event) => {
        const target = event.target;
        if (!(target instanceof HTMLElement)) {
            return;
        }

        if (target.id?.startsWith("add_transactions_")) {
            requestAnimationFrame(() => {
                applyTransactionFieldValidation();
            });
        }
    });
}

function initializeTransactionValidation() {
    bindTransactionValidationForDynamicRows();
    applyTransactionFieldValidation();

    const totalField = document.querySelector(TOTAL_AMOUNT_SELECTOR);
    if (totalField) {
        totalField.setAttribute("readonly", "true");
        totalField.style.backgroundColor = "#e9ecef";
        totalField.style.cursor = "not-allowed";
    }
}

function getDocumentationElements() {
    return {
        documentation: document.getElementById("documentation"),
        fileUpload: document.getElementById("file-upload"),
        fileList: document.getElementById("file-list"),
    };
}

function getOutOfPocketSectionElements() {
    const outOfPocketCheckbox = document.querySelector(OOP_CLAIM_SELECTOR);

    return {
        outOfPocketCheckbox,
        outOfPocketField: outOfPocketCheckbox?.closest(".mb-3") || null,
        transactions: document.getElementById("transactions"),
        documentation: document.getElementById("documentation"),
    };
}

function isOutOfPocketClaimSelected() {
    return Array.from(document.querySelectorAll(OOP_CLAIM_SELECTOR)).some(
        (checkbox) => checkbox.checked
    );
}

function syncProRataSelection() {
    if (!isOutOfPocketClaimSelected()) {
        return;
    }

    document.querySelectorAll(PRO_RATA_CLAIM_SELECTOR).forEach((checkbox) => {
        checkbox.checked = true;
    });
}

function placeOutOfPocketSections() {
    const { outOfPocketField, transactions, documentation } =
        getOutOfPocketSectionElements();

    if (!outOfPocketField) {
        return;
    }

    const sectionContainer = outOfPocketField.parentElement;
    const insertBeforeNode = outOfPocketField.nextSibling;

    const totalField = document.querySelector(TOTAL_AMOUNT_SELECTOR);
    const totalFieldWrapper =
        totalField?.closest(".mb-3") || totalField?.parentElement || null;

    if (transactions && transactions.parentElement !== sectionContainer) {
        sectionContainer.insertBefore(transactions, insertBeforeNode);
    }

    if (totalFieldWrapper && transactions.nextSibling !== totalFieldWrapper) {
        transactions.insertAdjacentElement("afterend", totalFieldWrapper);
    }

    if (documentation && documentation.parentElement !== sectionContainer) {
        sectionContainer.insertBefore(documentation, insertBeforeNode);
    }
}

function syncDocumentationRequirement() {
    const { fileUpload } = getDocumentationElements();
    const { transactions, documentation } = getOutOfPocketSectionElements();
    const isRequired = isOutOfPocketClaimSelected();

    placeOutOfPocketSections();
    syncProRataSelection();

    const totalField = document.querySelector(TOTAL_AMOUNT_SELECTOR);
    const totalFieldWrapper =
        totalField?.closest(".mb-3") || totalField?.parentElement || null;

    if (transactions) {
        transactions.classList.toggle("d-none", !isRequired);
    }

    if (totalFieldWrapper) {
        totalFieldWrapper.classList.toggle("d-none", !isRequired);
        if (!isRequired && totalField) {
            totalField.value = "";
        }
    }

    if (documentation) {
        documentation.classList.toggle("d-none", !isRequired);
    }

    if (!isRequired && fileUpload) {
        toggleFieldError(fileUpload, false);
    }
}

function initializeDocumentationRequirement() {
    const checkboxes = Array.from(document.querySelectorAll(OOP_CLAIM_SELECTOR));
    const proRataCheckboxes = Array.from(
        document.querySelectorAll(PRO_RATA_CLAIM_SELECTOR)
    );

    if (!checkboxes.length) {
        return;
    }

    placeOutOfPocketSections();

    checkboxes.forEach((checkbox) => {
        if (checkbox.dataset.documentationBound === "true") {
            return;
        }

        checkbox.dataset.documentationBound = "true";
        checkbox.addEventListener("change", syncDocumentationRequirement);
    });

    proRataCheckboxes.forEach((checkbox) => {
        if (checkbox.dataset.oopBound === "true") {
            return;
        }

        checkbox.dataset.oopBound = "true";
        checkbox.addEventListener("change", () => {
            if (isOutOfPocketClaimSelected()) {
                checkbox.checked = true;
            }
        });
    });

    syncDocumentationRequirement();
}

function hasUploadedDocumentation(claim) {
    const { fileList } = getDocumentationElements();

    return Boolean(
        claim.uploads?.length ||
            (fileList &&
                !fileList.classList.contains("d-none") &&
                fileList.children.length > 0)
    );
}

/**
 * Initializes a claim object.
 *
 * @param {Object} claim - The claim object to be initialized.
 * @returns {Object} The initialized claim object.
 */
export function customInitClaim(claim) {
    initializeDocumentationRequirement();
    initializeTransactionValidation();
    return claim;
}

/**
 * Custom function to load a member.
 *
 * @param {Object} claim - The member object to be loaded.
 * @returns {Object} - The loaded member object.
 */
export function customLoadMember(claim) {
    requestAnimationFrame(() => {
        applyTransactionFieldValidation();
    });

    return claim;
}

/**
 * Processes a claim for a custom member.
 *
 * @param {Object} claim - The claim to be processed.
 * @returns {Object} The processed claim.
 */
export function customProcessClaim(claim) {
    console.log("[Process claim]...");
    applyTransactionFieldValidation();

    const paymentMethod = document.getElementById("payment_method");
    const paymentToken = document.getElementById("payment_token");
    const paymentEmail = document.getElementById("payment_email");
    const paymentPhone = document.getElementById("payment_phone");
    const { documentation, fileUpload } = getDocumentationElements();

    if (!claim.data) {
        claim.data = {};
    }
    if (!claim.data.fields) {
        claim.data.fields = {};
    }

    // #transactions and #documentation are moved inside #claim-fields by
    // placeOutOfPocketSections(), so their inputs get captured by the
    // #claim-fields selector and land in claim.data.fields as duplicates.
    // Remove them here — transactions appear as numeric keys (item-id),
    // documentation inputs are identified via closest().
    if (claim.inputs?.fields) {
        claim.inputs.fields
            .filter(
                (f) =>
                    f.closest("#transactions") || f.closest("#documentation")
            )
            .forEach((field) => {
                const label = field.getAttribute("data-label") || field.name;
                const itemId = field.getAttribute("item-id");
                if (itemId) {
                    delete claim.data.fields[itemId];
                } else {
                    delete claim.data.fields[label];
                }
            });
    }

    const method = paymentMethod ? paymentMethod.value : "";
    const token = paymentToken ? paymentToken.value : "";
    const email = paymentEmail ? paymentEmail.value : "";
    const phone = paymentPhone ? paymentPhone.value : "";

    claim.data.fields["Payment Method"] = method;
    claim.data.fields["Payment Token"] = token;
    claim.data.fields["Payment Email"] = email;
    claim.data.fields["Payment Phone"] = phone;

    claim.data.payment_method = method;
    claim.data.payment_token = token;
    claim.data.payment_email = email;
    claim.data.payment_phone = phone;

    // Existing claims may already have documentation on file, but the edit UI
    // does not rehydrate uploaded documents into the client-side file list.
    if (
        isOutOfPocketClaimSelected() &&
        !claim.data.claim_id &&
        !hasUploadedDocumentation(claim)
    ) {
        claim.flags.valid = false;

        if (fileUpload && !fileUpload.value) {
            toggleFieldError(
                fileUpload,
                true,
                "Required for Out-of-Pocket Losses claim"
            );
        }

        if (documentation) {
            documentation.scrollIntoView({ behavior: "smooth", block: "start" });
        }
    }

    return claim;
}
